import { faChevronLeft } from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { QBox, QButton, QStack } from '@captivateiq/quotient';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';
import type { AttachmentMeta } from '@captivateiq/assistant/types';
import { getAgentConfig } from '@captivateiq/assistant/config/agentConfig';
import { SamplePromptCard } from './SamplePromptCard';
import { TabLayout } from '../../TabLayout';
import { expandedTextColumnSx } from '../../shared/expandedTextColumnSx';
import { AgentIntroduction } from '../shared/AgentIntroduction';
import { ExperimentalDisclaimer } from '../shared/ExperimentalDisclaimer';
import { MessageInput } from '../shared/MessageInput';

export const IntroView = () => {
  const { t } = useTranslation('assistant');
  const { selectedAgent, backToAgentBrowser, openChatAndSend, isExpanded } = useAIShellContext();

  const handleSubmit = useCallback(
    async (message: string, attachments?: AttachmentMeta[]) => {
      await openChatAndSend(message, attachments);
    },
    [openChatAndSend],
  );

  const prompts = useMemo(() => {
    if (!selectedAgent) return [];
    return getAgentConfig(selectedAgent, t).prompts;
  }, [selectedAgent, t]);

  return (
    <TabLayout>
      <QBox sx={expandedTextColumnSx(isExpanded)}>
        <QButton
          color="a"
          size="small"
          startIcon={faChevronLeft}
          sx={{ '& .MuiButton-startIcon': { fontSize: '12px' } }}
          variant="text"
          onClick={backToAgentBrowser}
        >
          {t('button_labels.back')}
        </QButton>
      </QBox>
      <QBox sx={{ flexGrow: 1 }} />
      <QBox sx={{ paddingBottom: '24px', ...expandedTextColumnSx(isExpanded) }}>
        <AgentIntroduction agentName={selectedAgent ?? ''} />
      </QBox>
      <QStack
        direction="column"
        spacing="10px"
        sx={{
          alignItems: 'stretch',
          ...expandedTextColumnSx(isExpanded),
        }}
      >
        {prompts.map((prompt) => (
          <SamplePromptCard key={prompt} prompt={prompt} />
        ))}
      </QStack>
      <QBox sx={{ marginTop: '10px', ...expandedTextColumnSx(isExpanded) }}>
        <MessageInput tall onSubmit={handleSubmit} />
      </QBox>
      <QBox sx={{ flexGrow: 1.8 }} />
      <QBox sx={{ paddingBottom: '16px', ...expandedTextColumnSx(isExpanded) }}>
        <ExperimentalDisclaimer />
      </QBox>
    </TabLayout>
  );
};
